<template>
    <div class="container mx-auto px-4">
        <h1 class="font-serif mb-8 md:text-5xl text-2xl text-center">
            {{ __('Rendelések') }}
        </h1>

        <nav class="flex mb-8">
            <router-link class="bg-white border border-slate-200 cursor-pointer flex flex-col items-center justify-center md:flex-row -mr-px p-2 rounded-bl rounded-tl text-center text-slate-800 text-sm"
                         v-bind:to="{ name : 'orders.table' }"
            >
                <c-icon class="h-6 md:mr-2 stroke-slate-800 w-6" icon="columns" />
                Táblázatos nézet
            </router-link>
            <span class="bg-highlight border border-slate-200 cursor-pointer flex flex-col items-center justify-center md:flex-row -mr-px p-2 text-center text-sm text-white">
                <c-icon class="h-6 md:mr-2 stroke-white w-6" icon="users" />
                Napi bontás
            </span>
            <router-link class="bg-white border border-slate-200 cursor-pointer flex flex-col items-center justify-center md:flex-row -mr-px p-2 text-center text-slate-800 text-sm"
                         v-bind:to="{ name : 'orders.byuser' }"
            >
                <c-icon class="h-6 md:mr-2 stroke-slate-800 w-6" icon="user" />
                Megrendelő szerint
            </router-link>
            <router-link class="bg-white border border-slate-200 cursor-pointer flex flex-col items-center justify-center md:flex-row p-2 rounded-br rounded-tr text-center text-slate-800 text-sm"
                         v-bind:to="{ name : 'orders.byproduct' }"
            >
                <c-icon class="h-6 md:mr-2 stroke-slate-800 w-6" icon="clipboard" />
                Termék összesítő
            </router-link>
        </nav>

        <div class="flex items-center mb-8">
            <c-control-text class="w-full" class-field="!h-10 !text-lg" class-group="bg-white border pl-2 rounded-none"
                            icon="search"
                            name="search"
                            v-bind:placeholder="__('Megrendelő keresése...')"
                            v-model="s"
                            v-on:keyup="search"
            />

            <c-button class="bg-slate-800 ml-4 shrink-0 text-white"
                      router
                      v-bind:disabled="orders.length === 0"
                      v-on:click="exportXls"
            >
                <c-icon class="h-4 mr-2 stroke-white w-4" icon="download" />{{ __('Exportálás') }}
            </c-button>
        </div>

        <div class="bg-highlight flex items-center justify-between mb-10 mt-8 px-2 py-4 text-white">
            <a class="cursor-pointer flex items-center text-lg text-white uppercase"
               v-on:click="prevDay"
            >
                <c-icon class="h-6 mr-2 stroke-white w-6" icon="chevron-left" />
                <span class="hidden md:inline">{{ __('Előző') }}</span>
            </a>
            <span class="font-serif text-3xl text-center">
                {{ DateTime.fromISO(currentDate).toFormat(localizationStore.dateFormat) }}
                <small class="capitalize font-sans text-base">
                    ({{ DateTime.fromISO(currentDate).setLocale(localizationStore.locale).toFormat('cccc') }})
                </small>
            </span>
            <a class="cursor-pointer flex items-center text-lg text-white uppercase"
               v-on:click="nextDay"
            >
                <span class="hidden md:inline">{{ __('Következő') }}</span>
                <c-icon class="h-6 ml-2 stroke-white w-6" icon="chevron-right" />
            </a>
        </div>

        <div v-if="orders.length > 0">
            <div class="mb-8" v-for="order in orders">
                <div class="bg-slate-100 border border-b-4 border-black flex font-bold mb-4 text-highlight text-sm">
                    <span class="border-black border-r p-2.5">
                        #{{ order.oid }} / {{ DateTime.fromISO(order.created_at).toFormat(localizationStore.dateTimeFormat) }}
                    </span>
                    <span class="border-black border-r p-2.5">
                        {{ order.user.prefix }}
                        <span class="uppercase">{{  order.user.lastname }}</span>
                        {{  order.user.firstname }}<br />
                        <small v-if="order.user.group">
                            ({{ __(':group csoport', { 'group' : order.user.group.name }) }})
                        </small>
                        <div class="flex items-center mt-2 text-slate-800" v-if="order.user.is_blacklisted">
                            <c-icon class="h-6 mr-2 stroke-error w-6" icon="check-circle" /> {{ __('Feketelistás') }}
                        </div>
                    </span>
                    <span class="ml-auto p-2.5 text-right">
                        {{ __('Termékek értéke') }}:
                        {{ currencyStore.format(dailyValue, 'huf') }}
                        <small class="block" v-if="order.ipn_status === 'FINISHED'">({{ __('Fizetve') }})</small>
                    </span>
                </div>
                <div>
                    <div v-for="product in order.items">
                        <div class="flex flex-col md:flex-row md:items-center mb-2 mr-4 pb-2"
                             v-if="product.day === currentDate.weekdayLong.toLowerCase()"
                        >
                            <img class="hidden md:block mr-4 max-w-32 w-full"
                                 v-bind:alt="product.product.name"
                                 v-bind:src="product.product.picture"
                            />
                            <div class="mr-8">
                                <strong class="block text-slate-800 text-sm">{{ product.product.name }}</strong>
                                <span class="block max-w-96 text-slate-500 text-xs">{{ product.product.description }}</span>
                            </div>
                            <div class="flex grow items-center mr-4 my-4">
                                <c-control-select class="lg:-mt-5 mr-4 shrink-0" class-field="!bg-slate-800 !text-white"
                                                  v-bind:label="__('Kiszállítási idő')"
                                                  v-bind:name="'delivery_time.' + product.id"
                                                  v-bind:options="deliveryTimeOptions"
                                                  v-if="order.user.role === 'reseller'"
                                                  v-model="product.delivery_time"
                                                  v-on:update:model-value="updateDeliveryTime(product)"
                                />
                            </div>
                            <div class="ml-auto">
                                <strong class="mr-4">{{ product.quantity }}x</strong>
                                <span class="line-through mr-2" v-if="product.price_original !== product.price_gross">
                                    {{ currencyStore.format(product.price_original, 'huf') }}
                                </span>
                                <span>{{ currencyStore.format(product.price_gross, 'huf') }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="border-t flex flex-col md:flex-row md:items-center mr-4 mt-2 pt-2"
                         v-if="order.value >= currencyStore.shipping_limits.zone && order.value < currencyStore.shipping_limits.free"
                    >
                        <div class="mr-4 max-w-32 w-full" />
                        <div class="mr-4">
                            <strong class="block text-slate-800 text-sm">{{ __('Kiszállítási költség') }}</strong>
                        </div>
                        <div class="md:ml-auto">
                            <strong class="mr-4">1x</strong>
                            <span>
                                {{ currencyStore.format(order.user.shipping_cost ?? currencyStore.shipping_costs[order.user.shipping_zone ?? 'default'], 'huf') }}
                            </span>
                        </div>
                    </div>
                    <div class="border-t flex flex-col md:flex-row md:items-center mr-4 mt-2 pt-2"
                         v-else
                    >
                        <div class="mr-4 max-w-32 w-full" />
                        <div class="mr-4">
                            <strong class="block text-slate-800 text-sm"
                                    v-if="order.value < currencyStore.shipping_limits.zone"
                            >
                                {{ __('Személyes átvétel') }}
                            </strong>
                            <strong class="block text-slate-800 text-sm"
                                    v-else
                            >
                                {{ __('Ingyenes szállítás') }}
                            </strong>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <span class="block font-serif text-center text-2xl" v-else>{{ __('Nincs találat') }}</span>
    </div>
</template>

<script setup lang="ts">
    import { computed, inject, onMounted, ref } from 'vue';
    import {
        CButton,
        CIcon,
        CControlSelect,
        CControlText,
    } from '@teamfurther/cinderblock';
    import { DateTime } from 'luxon';
    import debounce from 'debounce';
    import OrderRepository from '../../repositories/OrderRepository';
    import { useCurrencyStore } from '../../stores/currency';
    import { useLocalizationStore } from '../../stores/localization';
    import { useRequestStore } from '../../stores/request';

    const __ = inject('__');
    const currencyStore = useCurrencyStore();
    const currentDate = ref(DateTime.now().setLocale('en'));
    const dailyValue = computed(() => {
        let sum = 0;

        orders.value.forEach((order) => {
            order.items.forEach((product) => {
                if (product.day === currentDate.value.weekdayLong.toLowerCase()) {
                    sum += product.quantity * product.price_gross;
                }
            });
        });

        return sum;
    });
    const deliveryTimeOptions = [
        { label: '07:00', value: '0700' },
        { label: '07:30', value: '0730' },
        { label: '08:00', value: '0800' },
        { label: '08:30', value: '0830' },
        { label: '09:00', value: '0900' },
        { label: '09:30', value: '0930' },
        { label: '10:00', value: '1000' },
        { label: '10:30', value: '1030' },
        { label: '11:00', value: '1100' },
        { label: '11:30', value: '1130' },
        { label: '12:00', value: '1200' },
        { label: '12:30', value: '1230' },
        { label: '13:00', value: '1300' },
        { label: '13:30', value: '1330' },
        { label: '14:00', value: '1400' },
        { label: '14:30', value: '1430' },
        { label: '15:00', value: '1500' },
        { label: '15:30', value: '1530' },
        { label: '16:00', value: '1600' },
    ];
    const localizationStore = useLocalizationStore();
    const orderRepository = new OrderRepository();
    const orders = ref([]);
    const requestStore = useRequestStore();
    const s = ref<string>('');
    const sort = ref<string>('delivery_time');

    async function exportXls() {
        requestStore.post('orders/export/daily', {
            date: currentDate.value.toFormat('yyyy-MM-dd'),
        }).then((response : any) => {
            const blob = new Blob([
                new Uint8Array([0xEF, 0xBB, 0xBF]), // UTF-8 BOM
                response.data
            ]);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;

            a.download = 'napi_'+ currentDate.value.toFormat('yyyy-MM-dd') +'.csv';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
        });
    }

    async function getOrders() {
        orders.value = await orderRepository.index({
            filters: {
                day: currentDate.value.weekdayLong.toLowerCase(),
                week: currentDate.value.year + '.' + currentDate.value.weekNumber,
                user: s.value,
            },
            include: ['items', 'items.product', 'user', 'user.group'],
            sort: sort.value,
        });
    }

    async function nextDay() {
        currentDate.value = currentDate.value.plus({ days: 1 });

        if (currentDate.value.weekday === 6) {
            currentDate.value = currentDate.value.plus({ day: 2 });
        }

        await getOrders();
    }

    async function prevDay() {
        currentDate.value = currentDate.value.minus({ days: 1 });

        if (currentDate.value.weekday === 7) {
            currentDate.value = currentDate.value.minus({ day: 2 });
        }

        await getOrders();
    }

    async function updateDeliveryTime(product) {
        requestStore.put('orders/update-delivery-time', {
            id: product.id,
            delivery_time: product.delivery_time
        }).catch(error => reject(error));
    }

    const search = debounce(async () => {
        await getOrders();
    }, 500);

    onMounted(async () => {
        await getOrders();
    });
</script>
